import {DateTime} from "luxon";

export const formValidation = ({setError, clearError, inputEntries, getValue, getValues}, {getValidator}) => {
    const regexes = {
        email: /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/,
        date: /^[0-9]{4}-[0-9]{1,2}-[0-9]{1,2}$/,
        iban: /^[a-zA-Z]{2}[0-9]{2}[a-zA-Z0-9]{4}[0-9]{7}([a-zA-Z0-9]?){0,16}$/,
        postalCode: /^[0-9]{5}$/,
        personalBirthNumber: /^[0-9]{6}\/[0-9]{3,4}$/,
        integer: /^\d+$/,
        float: /^\d+([.,]\d{0,2})?$/,
        float2: /^\d+[.,]\d{2}$/,
        floatSp: /^[\d ]+([.,]\d{0,2})?$/,
        float6: /^[\d ]+([.,]\d{0,6})?$/,
        contractNumber1: /^SPO-V0[12]-\d{4}(-Z)?$/,
        contractNumber: /^SPO-V0[123]-\d{5}(-Z)?$/,
        contractNumber3: /^SPO-V03-\d{5}(-Z)?$/,
        requestIdentifier: /^\d{12}$/,
    };

    const test = (test, message) => !!test ? null : (message || true);
    const regex = (key, value, message) => test(regexes[key].test(value), message);
    const pf = value => parseFloat(value.replace(',', '.').replace(' ', ''));
    const rules = {
        any: () => null,
        empty: message => value => test(!value.length, message),
        required: message => value => test(value.length, message),
        requiredRadio: message => value => test(value && value !== 'no-option', message),
        accepted: message => value => test(value, message),
        requiredKey: (key, message) => (value, values) => test(values[key].length, message),
        requiredRadioKey: (key, message) => (value, values) => test(values[key] && values[key] !== 'no-option', message),
        acceptedKey: (key, message) => (value, values) => test(values[key], message),
        same: (key, message) => (value, values) => test(value === values[key], message),
        equal: (to, message) => value => test(value === to, message),
        lessThanKey: (key, message) => (value, values) => test(pf(value) < pf(values[key]), message),
        lessThanEqKey: (key, message) => (value, values) => test(pf(value) <= pf(values[key]), message),
        max: (to, message) => value => test(value <= to, message),
        min: (to, message) => value => test(value >= to, message),
        cmp: {
            eq: (key, to, message) => (value, values) => test(values[key] === to, message),
            lte: (key, to, message) => (value, values) => test(pf(values[key]) <= to, message),
            date: {
                maxYears_: (key, to, message) => (value, values) =>
                    test(-DateTime.fromObject(values[key]).diffNow().as('year') < to, message),
                minYears_: (key, to, message) => (value, values) =>
                    test(-DateTime.fromObject(values[key]).diffNow().as('year') >= to, message),
                maxYears: (to, message) => value =>
                    test(DateTime.fromObject({...value, year: parseInt(value.year) + to}) >= DateTime.now(), message),
                minYears: (to, message) => value =>
                    test(DateTime.fromObject({...value, year: parseInt(value.year) + to}) <= DateTime.now(), message),
            },
            integer: {
                max: (to, message) => value =>
                    test(Number(value) < to, message),
            },
        },

        email: message => value => regex('email', value, message),
        date: message => ({day, month, year}) => regex('date', `${year}-${month}-${day}`, message),
        iban: message => value => regex('iban', value, message),
        postalCode: message => value => regex('postalCode', value, message),
        personalBirthNumber: message => value => regex('personalBirthNumber', value, message),
        integer: message => value => regex('integer', value, message),
        float: message => value => regex('float', value, message),
        floatSp: message => value => regex('floatSp', value, message),
        float2: message => value => regex('float2', value, message),
        float6: message => value => regex('float6', value, message),
        contractNumber1: message => value => regex('contractNumber1', value, message),
        contractNumber: message => value => regex('contractNumber', value, message),
        contractNumber3: message => value => regex('contractNumber3', value, message),
        requestIdentifier: message => value => regex('requestIdentifier', value, message),

        and: (...args) => (value, values) => args.reduce((acc, validator) => acc || validator(value, values), null),
        or: (...args) => (value, values) => args.reduceRight(
            (acc, validator) => acc && (validator(value, values) || null),
            true
        ),
        cond: (condition, trueValidator, falseValidator) => (value, values) => {
            //console.log(value, !condition(value, values), trueValidator(value, values), falseValidator(value, values));
            return !condition(value, values) ? trueValidator(value, values) : falseValidator(value, values)
        },
    };

    const validator = getValidator(rules);

    const check = (key) => {
        try {
            return validator[key](getValue(key), getValues());
        } catch (e) {
            // console.error('Validation error', key);
            throw e;
        }
    }

    const validate = (key) => {
        // console.log(key, getValue(key));
        const error = check(key);
        if (error)
            setError(key, error);
        else
            clearError(key);
        return error;
    };

    const validateAll = () => {
        let error = null;
        inputEntries.forEach(([key]) => {
            const newError = validate(key);
            error = error || newError;
        })
        return !error;
    }

    return {
        check,
        validate,
        validateAll,
    };
};
